import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mic2, Music4, Scissors, SlidersHorizontal, Wand2, PenLine, Sparkles, Disc3, Headphones } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import ShowcaseSection from "@/components/creator/ShowcaseSection";
import HowItWorksDemo from "@/components/creator/HowItWorksDemo";
import { proStudioPath, getCurrentSongId } from "@/lib/studioMode";

const WORKFLOW = [
  { icon: PenLine, label: "Write" },
  { icon: Sparkles, label: "Generate" },
  { icon: Mic2, label: "Record" },
  { icon: Headphones, label: "Mix" },
  { icon: Disc3, label: "Master" },
];

const CARDS = [
  { key: "song", icon: Music4, title: "AI Song Creator", body: "Turn your lyrics into music.", to: "/create/song", accent: "from-blue-500 to-violet-600", glow: "rgba(99,102,241,0.35)" },
  { key: "vocal", icon: Mic2, title: "Vocal Studio", body: "Record and professionally process vocals.", to: "/create/record", accent: "from-rose-500 to-red-500", glow: "rgba(244,63,94,0.3)" },
  { key: "stems", icon: Scissors, title: "Stem Separator", body: "Separate vocals and instruments.", intent: "stems", accent: "from-cyan-400 to-sky-500", glow: "rgba(34,211,238,0.3)" },
  { key: "master", icon: Wand2, title: "AI Mastering", body: "Finish your song for streaming.", to: "/create/master", accent: "from-emerald-400 to-teal-500", glow: "rgba(52,211,153,0.3)" },
  { key: "pro", icon: SlidersHorizontal, title: "Advanced Studio", body: "Need more control? Open the complete 64-channel studio.", pro: true, accent: "from-amber-400 to-orange-500", glow: "rgba(251,191,36,0.3)" },
];

const CARD_INIT = { opacity: 0, y: 24 };
const CARD_IN = { opacity: 1, y: 0 };

function HomeCard({ card, i }) {
  const Icon = card.icon;
  const songId = getCurrentSongId();
  const to = card.pro ? proStudioPath(songId) : card.to || proStudioPath(songId, card.intent);
  return (
    <motion.div initial={CARD_INIT} animate={CARD_IN} transition={{ delay: 0.25 + i * 0.07, duration: 0.45 }}>
      <Link
        to={to}
        data-testid={`home-card-${card.key}`}
        className="group relative block h-full overflow-hidden rounded-3xl border border-white/[0.1] bg-[#0a0d1c]/80 p-6 transition-transform duration-300 hover:-translate-y-1.5"
        style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)" }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
          style={{ background: card.glow }}
        />
        <div className={`mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br ${card.accent} text-white shadow-lg`}>
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="font-display text-lg font-semibold">{card.title}</h3>
        <p className="mt-2 text-[15px] leading-relaxed text-white/80">{card.body}</p>
        <span className="mt-5 inline-flex items-center gap-1 font-display text-[13px] font-semibold uppercase tracking-[0.14em] text-blue-200 transition-colors group-hover:text-white">
          Open <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </span>
      </Link>
    </motion.div>
  );
}

/** ELPM STUDIO homepage — the Creator front door. */
export default function CreatorHome() {
  return (
    <CreatorShell testId="creator-home" wide>
      <section className="relative pt-6 sm:pt-12">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="font-display text-[13px] font-semibold uppercase tracking-[0.3em] text-blue-200">
          ELPM STUDIO
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.05 }}
          data-testid="home-headline"
          className="mt-4 max-w-4xl font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
        >
          Make Your Song.<br />
          <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Your Way.</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-5 max-w-xl text-base text-white/85 md:text-lg">
          From lyrics to finished music — right in your browser. No instruments, no experience needed.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.22 }} className="mt-9 flex flex-wrap items-center gap-4">
          <Link
            to="/create"
            data-testid="home-start-creating"
            className="group relative inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-9 py-4 font-display text-base font-bold tracking-wide text-white shadow-[0_12px_40px_rgba(99,102,241,0.45)] transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98] md:text-lg"
          >
            START CREATING
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/create/projects" data-testid="home-my-projects" className="font-display text-[14px] font-semibold text-white/85 underline-offset-4 transition-colors hover:text-white hover:underline">
            Continue in My Projects
          </Link>
        </motion.div>

        <motion.ol
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.35 }}
          data-testid="home-workflow-strip"
          className="mt-12 flex flex-wrap items-center gap-3"
        >
          {WORKFLOW.map((w, i) => {
            const Icon = w.icon;
            return (
              <li key={w.label} className="flex items-center gap-3">
                <span className="flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 font-display text-[13px] font-semibold uppercase tracking-[0.16em] text-white/90">
                  <Icon className="h-4 w-4 text-blue-300" /> {w.label}
                </span>
                {i < WORKFLOW.length - 1 ? <ArrowRight className="h-3.5 w-3.5 text-white/40" /> : null}
              </li>
            );
          })}
        </motion.ol>
      </section>

      <section className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="home-cards">
        {CARDS.map((c, i) => <HomeCard key={c.key} card={c} i={i} />)}
      </section>

      <ShowcaseSection />
      <HowItWorksDemo />
    </CreatorShell>
  );
}
